import { useCallback, useEffect, useState } from 'react'
import { PageHeader, StatusBadge, colors } from '../../components/ui'
import { api } from '../../services/api'
import { livenessFromLastSeen, relativeTime } from './fleet-helpers'

// Environment -> Tenancy topology (Phase 3 Slice 4). medflow_admin-only view of
// which tenancies are bound to which Environments, plus an "Unbound" bucket for
// tenancies that have been minted or moved off an Environment (environmentId
// null). Central holds Environments + Tenancies but not Sites, so Site leaves
// are out of scope here (optional Slice 5).

type TenancyStatus = 'active' | 'suspended' | 'expired' | 'revoked'

interface TopologySite {
  readonly id: string
  readonly name: string
}

interface TopologyTenancy {
  readonly id: string
  readonly name: string
  readonly organizationName: string | null
  readonly licenseStatus: TenancyStatus
  readonly licenseTier: string | null
  readonly activatedAt: string | null
  // Slice 5: Site leaves reported by the edge for this tenancy (empty when the
  // edge has not reported sites, or for unbound tenancies).
  readonly sites: readonly TopologySite[]
}

interface TopologyEnvironmentNode {
  readonly environment: {
    readonly id: string
    readonly name: string
    readonly environmentType: string
    readonly lastSeenAt: string | null
    readonly provisioningStatus: string | null
  }
  readonly tenancies: readonly TopologyTenancy[]
}

interface TopologyResponse {
  readonly environments: readonly TopologyEnvironmentNode[]
  readonly unbound: readonly TopologyTenancy[]
}

// Map a license status onto a StatusBadge palette key.
function licenseBadge(status: TenancyStatus): 'active' | 'warning' | 'inactive' {
  if (status === 'active') return 'active'
  if (status === 'suspended' || status === 'expired') return 'warning'
  return 'inactive'
}

function TenancyRow({ t }: { readonly t: TopologyTenancy }) {
  return (
    <div style={{ borderTop: `1px solid ${colors.border}` }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          padding: '0.6rem 0.75rem',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, color: colors.textHeading }}>{t.name}</div>
          <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>{t.organizationName ?? '—'}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
          {t.licenseTier && <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>{t.licenseTier}</span>}
          <StatusBadge status={licenseBadge(t.licenseStatus)} label={t.licenseStatus} />
        </div>
      </div>
      {/* Slice 5: reported Site leaves, indented under the tenancy. */}
      {t.sites.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 0 0.5rem 1.75rem' }}>
          {t.sites.map((s) => (
            <li
              key={s.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.2rem 0',
                fontSize: '0.8125rem',
                color: colors.text,
              }}
            >
              <span style={{ color: colors.textMuted }}>↳</span>
              {s.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function cardStyle(): React.CSSProperties {
  return {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '0.5rem',
    overflow: 'hidden',
  }
}

export function FleetTopologyPage() {
  const [data, setData] = useState<TopologyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<TopologyResponse>('/fleet/topology')
      setData(res.data ?? { environments: [], unbound: [] })
    } catch {
      setError('Failed to load topology')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const envCount = data?.environments.length ?? 0

  return (
    <div>
      <PageHeader
        title="Topology"
        count={envCount}
        subtitle="Tenancies grouped by the Environment that hosts them"
        onRefresh={() => void load()}
      />

      {loading && <p style={{ color: colors.textMuted }}>Loading…</p>}
      {error && (
        <p style={{ color: colors.danger, background: colors.bgError, padding: '0.75rem', borderRadius: '0.5rem' }}>
          {error}
        </p>
      )}

      {!loading && !error && data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {data.environments.map((node) => (
            <div key={node.environment.id} style={cardStyle()}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  background: colors.bgMuted,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: colors.textHeading }}>{node.environment.name}</div>
                  <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
                    {node.environment.environmentType} · {node.tenancies.length}{' '}
                    {node.tenancies.length === 1 ? 'tenancy' : 'tenancies'} · last seen{' '}
                    {relativeTime(node.environment.lastSeenAt)}
                  </div>
                </div>
                {node.environment.provisioningStatus === 'awaiting_dns' ? (
                  <StatusBadge status="warning" label="Awaiting DNS" />
                ) : node.environment.provisioningStatus === 'installing' ? (
                  <StatusBadge status="in_progress" label="Installing" />
                ) : (
                  <StatusBadge status={livenessFromLastSeen(node.environment.lastSeenAt)} />
                )}
              </div>
              {node.tenancies.length === 0 ? (
                <div style={{ padding: '0.75rem', color: colors.textMuted, fontSize: '0.875rem' }}>
                  No tenancies on this environment.
                </div>
              ) : (
                node.tenancies.map((t) => <TenancyRow key={t.id} t={t} />)
              )}
            </div>
          ))}

          {data.unbound.length > 0 && (
            <div style={cardStyle()}>
              <div style={{ padding: '0.75rem', background: colors.bgMuted }}>
                <div style={{ fontWeight: 600, color: colors.textHeading }}>Unbound</div>
                <div style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
                  {data.unbound.length} {data.unbound.length === 1 ? 'tenancy' : 'tenancies'} not bound to an
                  Environment (mint or moved off — re-activate on a target to bind)
                </div>
              </div>
              {data.unbound.map((t) => (
                <TenancyRow key={t.id} t={t} />
              ))}
            </div>
          )}

          {envCount === 0 && data.unbound.length === 0 && (
            <p style={{ color: colors.textMuted }}>No environments or tenancies yet.</p>
          )}
        </div>
      )}
    </div>
  )
}
